// src/features/analytics/components/AnalyticsOverviewPage.tsx

import { Stack } from '@mantine/core';
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags';
import { useAnalyticsRange } from '../useAnalyticsRange';
import { useAnalyticsScope } from '../useAnalyticsScope';
import { useAnalyticsOverview } from '../useAnalyticsOverview';
import { useAnalyticsConcurrency } from '../useAnalyticsConcurrency';
import { useAnalyticsPlaybackDaily } from '../useAnalyticsPlaybackDaily';
import { returningShare } from '../viewerMix';
import { AnalyticsCoverageNotice } from './AnalyticsCoverageNotice';
import { AnalyticsErrorState } from './AnalyticsErrorState';
import { ConcurrencyTrendCard } from './ConcurrencyTrendCard';
import { LiveAudienceCard } from './LiveAudienceCard';
import { OverviewCards } from './OverviewCards';
import { PlaybackTrendCard } from './PlaybackTrendCard';
import { ViewerMixTrendCard } from './ViewerMixTrendCard';

/** Composition only — cards + trends + live. */
export function AnalyticsOverviewPage() {
  const { search, range } = useAnalyticsRange();
  const { scope } = useAnalyticsScope(search.scope);
  const overviewQuery = useAnalyticsOverview(scope, range);
  const playbackDailyQuery = useAnalyticsPlaybackDaily(scope, range);
  const concurrencyQuery = useAnalyticsConcurrency(scope, range, 'auto');

  // Viewer mix rides the already-fetched playback/daily response (metrics
  // expansion: extend, don't refetch).
  const viewerMixShare = returningShare(playbackDailyQuery.playbackDaily?.data.points ?? []);

  return (
    <Stack gap="md">
      {overviewQuery.isError ? (
        <AnalyticsErrorState error={overviewQuery.error} onRetry={overviewQuery.refetch} />
      ) : (
        <>
          {overviewQuery.overview?.coverage && (
            <AnalyticsCoverageNotice
              coverage={overviewQuery.overview.coverage}
              freshness={overviewQuery.overview.freshness}
            />
          )}
          <OverviewCards
            overview={overviewQuery.overview?.data ?? null}
            isLoading={overviewQuery.isLoading}
            returningShare={viewerMixShare}
            returningShareLoading={playbackDailyQuery.isLoading}
          />
        </>
      )}

      <PlaybackTrendCard
        playbackDaily={playbackDailyQuery.playbackDaily}
        range={range}
        rangeTotals={overviewQuery.overview?.data.playback ?? null}
        isLoading={playbackDailyQuery.isLoading}
        isError={playbackDailyQuery.isError}
        error={playbackDailyQuery.error}
        onRetry={playbackDailyQuery.refetch}
      />

      {FEATURE_FLAGS.analyticsExpansionEnabled && (
        <ViewerMixTrendCard
          playbackDaily={playbackDailyQuery.playbackDaily}
          isLoading={playbackDailyQuery.isLoading}
          isError={playbackDailyQuery.isError}
          error={playbackDailyQuery.error}
          onRetry={playbackDailyQuery.refetch}
        />
      )}

      <ConcurrencyTrendCard
        concurrency={concurrencyQuery.concurrency}
        isLoading={concurrencyQuery.isLoading}
        isError={concurrencyQuery.isError}
        error={concurrencyQuery.error}
        onRetry={concurrencyQuery.refetch}
      />

      <LiveAudienceCard scope={scope} />
    </Stack>
  );
}
